import React, { useEffect, useState, Component } from 'react';
import { Box, Flex, Text, Input, Button, FormControl, FormLabel, useToast, Badge, Divider, InputGroup, InputRightElement, IconButton } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MapPin, CheckCircle, Tag, X } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { PromoCode } from '../types';
export const Checkout: React.FC = () => {
  const {
    cart,
    user,
    clearCart,
    addOrder,
    updateUser,
    validatePromoCode,
    adminSettings
  } = useAppContext();
  const navigate = useNavigate();
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    mobile: user?.mobile || '',
    address: user?.address || '',
    city: 'Ghazipur',
    pincode: user?.pincode || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<PromoCode | null>(null);
  const [promoError, setPromoError] = useState('');
  useEffect(() => {
    if (cart.length === 0) navigate('/cart');
  }, [cart, navigate]);
  const totalMRP = cart.reduce((sum, item) => sum + item.discountPrice * item.quantity, 0);
  let promoDiscountAmount = 0;
  if (appliedPromo) {
    if (appliedPromo.discountType === 'percentage') {
      promoDiscountAmount = Math.round(totalMRP * appliedPromo.discountValue / 100);
    } else {
      promoDiscountAmount = appliedPromo.discountValue;
    }
    // Ensure discount doesn't exceed total
    if (promoDiscountAmount > totalMRP) promoDiscountAmount = totalMRP;
  }
  const finalAmount = totalMRP - promoDiscountAmount + adminSettings.deliveryCharge;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({
    ...prev,
    [e.target.name]: e.target.value
  }));
  const handleApplyPromo = () => {
    setPromoError('');
    if (!promoInput.trim()) return;
    const productIds = cart.map(item => item.id);
    const result = validatePromoCode(promoInput, productIds);
    if (result.valid && result.promo) {
      setAppliedPromo(result.promo);
      toast({
        title: 'Promo code applied!',
        status: 'success',
        duration: 2000
      });
    } else {
      setPromoError(result.error || 'Invalid promo code');
      setAppliedPromo(null);
    }
  };
  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError('');
  };
  const handlePlaceOrder = () => {
    if (!formData.name || !formData.mobile || !formData.address || !formData.pincode) {
      toast({
        title: 'Please fill all fields',
        status: 'error',
        duration: 2000
      });
      return;
    }
    if (formData.city.toLowerCase() !== 'ghazipur') {
      toast({
        title: 'Delivery available only in Ghazipur',
        status: 'error',
        duration: 3000
      });
      return;
    }
    if (formData.mobile.length < 10) {
      toast({
        title: 'Enter valid mobile number',
        status: 'error',
        duration: 2000
      });
      return;
    }
    setIsSubmitting(true);
    updateUser({
      ...formData,
      email: user?.email || ''
    });
    const orderId = `SKY${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
    const orderDate = new Date().toISOString().split('T')[0];
    const newOrder = {
      id: orderId,
      items: [...cart],
      total: finalAmount,
      status: 'Pending' as const,
      date: orderDate,
      customer: formData,
      promoCode: appliedPromo?.code,
      promoDiscount: promoDiscountAmount
    };
    addOrder(newOrder);
    const itemsList = cart.map(item => `- ${item.quantity}x ${item.title} (₹${item.discountPrice})`).join('\n');
    let message = `*New Order – Sky Brand*\nOrder ID: ${orderId}\nCustomer Name: ${formData.name}\nMobile: ${formData.mobile}\nAddress: ${formData.address}, ${formData.city}, ${formData.pincode}\n\n*Products:*\n${itemsList}\n\nSubtotal: ₹${totalMRP}`;
    if (appliedPromo) message += `\nPromo Discount (${appliedPromo.code}): -₹${promoDiscountAmount}`;
    if (adminSettings.deliveryCharge > 0) message += `\nDelivery Charge: ₹${adminSettings.deliveryCharge}`;
    message += `\n*Total Amount: ₹${finalAmount}*\nOrder Date: ${orderDate}\nOrder Status: Pending\nPayment Mode: Cash On Delivery (COD)`;
    const whatsappUrl = `https://wa.me/916388437291?text=${encodeURIComponent(message)}`;
    clearCart();
    toast({
      title: 'Order Placed Successfully!',
      description: 'Redirecting to WhatsApp...',
      status: 'success',
      duration: 3000
    });
    setTimeout(() => {
      if (adminSettings.whatsappNotifications) window.open(whatsappUrl, '_blank');
      navigate('/orders');
      setIsSubmitting(false);
    }, 1500);
  };
  return <Box bg="gray.50" minH="100%" p={4} pb="100px" className={adminSettings.animationsEnabled ? 'animate-fade-in' : ''} data-id="element-430">
      <Text fontSize="xl" fontWeight="800" mb={4} color="gray.800" data-id="element-431">
        Checkout
      </Text>

      {/* Delivery Address */}
      <Box bg="white" p={4} borderRadius="16px" shadow="sm" mb={4} data-id="element-432">
        <Flex align="center" mb={4} data-id="element-433">
          <MapPin size={20} color={adminSettings.accentColor} data-id="element-434" />
          <Text ml={2} fontWeight="700" color="gray.800" data-id="element-435">
            Delivery Address
          </Text>
        </Flex>
        <FormControl mb={3} isRequired data-id="element-436">
          <FormLabel fontSize="xs" fontWeight="600" data-id="element-437">
            Full Name
          </FormLabel>
          <Input name="name" value={formData.name} onChange={handleChange} placeholder="Enter your name" size="sm" borderRadius="10px" data-id="element-438" />
        </FormControl>
        <FormControl mb={3} isRequired data-id="element-439">
          <FormLabel fontSize="xs" fontWeight="600" data-id="element-440">
            Mobile Number
          </FormLabel>
          <Input name="mobile" type="tel" value={formData.mobile} onChange={handleChange} placeholder="10-digit mobile number" size="sm" borderRadius="10px" data-id="element-441" />
        </FormControl>
        <FormControl mb={3} isRequired data-id="element-442">
          <FormLabel fontSize="xs" fontWeight="600" data-id="element-443">
            Full Address
          </FormLabel>
          <Input name="address" value={formData.address} onChange={handleChange} placeholder="House no, Building, Street, Area" size="sm" borderRadius="10px" data-id="element-444" />
        </FormControl>
        <Flex gap={3} mb={1} data-id="element-445">
          <FormControl isRequired data-id="element-446">
            <FormLabel fontSize="xs" fontWeight="600" data-id="element-447">
              City
            </FormLabel>
            <Input name="city" value={formData.city} isReadOnly bg="gray.100" size="sm" borderRadius="10px" data-id="element-448" />
            <Text fontSize="10px" color="red.500" mt={1} data-id="element-449">
              *Delivery only in Ghazipur
            </Text>
          </FormControl>
          <FormControl isRequired data-id="element-450">
            <FormLabel fontSize="xs" fontWeight="600" data-id="element-451">
              Pincode
            </FormLabel>
            <Input name="pincode" type="number" value={formData.pincode} onChange={handleChange} placeholder="e.g. 233001" size="sm" borderRadius="10px" data-id="element-452" />
          </FormControl>
        </Flex>
      </Box>

      {/* Promo Code Section */}
      {adminSettings.promoSystemEnabled && <Box bg="white" p={4} borderRadius="16px" shadow="sm" mb={4} data-id="element-453">
          <Flex align="center" mb={3} data-id="element-454">
            <Tag size={18} color={adminSettings.accentColor} data-id="element-455" />
            <Text ml={2} fontWeight="700" color="gray.800" data-id="element-456">
              Apply Promo Code
            </Text>
          </Flex>

          {!appliedPromo ? <Box data-id="element-457">
              <Flex gap={2} data-id="element-458">
                <Input placeholder="Enter code" value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} size="sm" borderRadius="10px" textTransform="uppercase" data-id="element-459" />
                <Button size="sm" bg={adminSettings.accentColor} color="white" onClick={handleApplyPromo} borderRadius="10px" px={6} data-id="element-460">
                  Apply
                </Button>
              </Flex>
              {promoError && <Text color="red.500" fontSize="xs" mt={2} data-id="element-461">
                  {promoError}
                </Text>}
            </Box> : <Flex align="center" justify="space-between" bg="green.50" p={3} borderRadius="10px" borderWidth="1px" borderColor="green.200" data-id="element-462">
              <Box data-id="element-463">
                <Flex align="center" gap={2} data-id="element-464">
                  <CheckCircle size={16} color="var(--chakra-colors-green-500)" data-id="element-465" />
                  <Text fontWeight="700" color="green.700" fontSize="sm" data-id="element-466">
                    {appliedPromo.code} Applied
                  </Text>
                </Flex>
                <Text fontSize="xs" color="green.600" mt={0.5} data-id="element-467">
                  You saved ₹{promoDiscountAmount}!
                </Text>
              </Box>
              <IconButton aria-label="Remove promo" icon={<X size={16} data-id="element-469" />} size="sm" variant="ghost" colorScheme="red" onClick={handleRemovePromo} data-id="element-468" />
            </Flex>}
        </Box>}

      {/* Order Summary */}
      <Box bg="white" p={4} borderRadius="16px" shadow="sm" mb={4} data-id="element-470">
        <Text fontWeight="700" mb={3} color="gray.800" data-id="element-471">
          Order Summary
        </Text>
        {cart.map(item => <Flex key={item.id} justify="space-between" mb={2} data-id="element-472">
            <Text fontSize="xs" color="gray.600" noOfLines={1} flex={1} data-id="element-473">
              {item.quantity}x {item.title}
            </Text>
            <Text fontSize="xs" fontWeight="600" ml={4} data-id="element-474">
              ₹{item.discountPrice * item.quantity}
            </Text>
          </Flex>)}
        <Divider my={3} data-id="element-475" />
        <Flex justify="space-between" mb={2} data-id="element-476">
          <Text fontSize="sm" color="gray.600" data-id="element-477">
            Subtotal
          </Text>
          <Text fontSize="sm" fontWeight="600" data-id="element-478">
            ₹{totalMRP}
          </Text>
        </Flex>
        {appliedPromo && <Flex justify="space-between" mb={2} data-id="element-479">
            <Text fontSize="sm" color="green.500" data-id="element-480">
              Promo Discount
            </Text>
            <Text fontSize="sm" fontWeight="700" color="green.500" data-id="element-481">
              -₹{promoDiscountAmount}
            </Text>
          </Flex>}
        <Flex justify="space-between" mb={3} data-id="element-482">
          <Text fontSize="sm" color="gray.600" data-id="element-483">
            Delivery Charge
          </Text>
          <Text fontSize="sm" fontWeight="600" data-id="element-484">
            {adminSettings.deliveryCharge === 0 ? 'Free' : `₹${adminSettings.deliveryCharge}`}
          </Text>
        </Flex>
        <Divider mb={3} data-id="element-485" />
        <Flex justify="space-between" align="center" data-id="element-486">
          <Text fontWeight="800" fontSize="md" data-id="element-487">
            Total Amount
          </Text>
          <Text fontWeight="800" fontSize="xl" color={adminSettings.accentColor} data-id="element-488">
            ₹{finalAmount}
          </Text>
        </Flex>
      </Box>

      {/* Payment Method */}
      <Box bg="white" p={4} borderRadius="16px" shadow="sm" mb={4} data-id="element-489">
        <Text fontWeight="700" mb={3} color="gray.800" data-id="element-490">
          Payment Method
        </Text>
        <Flex align="center" p={3} borderWidth="2px" borderColor={adminSettings.accentColor} borderRadius="12px" bg="brand.50" data-id="element-491">
          <CheckCircle size={20} color={adminSettings.accentColor} data-id="element-492" />
          <Text ml={3} fontWeight="600" color="gray.800" data-id="element-493">
            Cash On Delivery (COD)
          </Text>
        </Flex>
      </Box>

      {/* Place Order Button */}
      <Box position="fixed" bottom="72px" w="full" maxW="480px" bg="white" p={3} shadow="0 -4px 20px rgba(0,0,0,0.08)" zIndex={10} left="50%" transform="translateX(-50%)" data-id="element-494">
        <Button bg={adminSettings.accentColor} color="white" w="full" size="lg" onClick={handlePlaceOrder} isLoading={isSubmitting} loadingText="Processing..." borderRadius="12px" fontWeight="700" _hover={{
        opacity: 0.9
      }} data-id="element-495">
          Confirm Order (₹{finalAmount})
        </Button>
      </Box>
    </Box>;
};